import { unstable_cache } from 'next/cache';
import { cache } from 'react';

import { api } from '@/lib/api';
import { FILTER_OPTIONS_REVALIDATE_SECONDS } from '../consts/SEO_CACHE';
import type { CatalogFilterLockedFacets, CatalogFilterPageKind } from '../types';
import {
  fetchFanficsFilterOptions,
  type FanficsFilterOptions,
} from './fetch-fanfics-filter-options.util';
import type { CatalogFilterFetchMode } from './build-catalog-filter-fetch-plan.util';

interface FetchFanficsFilterOptionsCachedParams {
  type: 'fanfic' | 'original';
  pageKind?: CatalogFilterPageKind;
  fetchMode?: CatalogFilterFetchMode;
  lockedFacets?: CatalogFilterLockedFacets;
  selectedCharacterIds?: string[];
  selectedTagIds?: string[];
}

const buildFilterOptionsCacheKeyParts = (
  params: FetchFanficsFilterOptionsCachedParams,
): string[] => [
  'fanfics-filter-options',
  params.type,
  params.pageKind ?? 'catalog',
  params.fetchMode ?? 'ssr',
  JSON.stringify(params.lockedFacets ?? {}),
  (params.selectedCharacterIds ?? []).join(','),
  (params.selectedTagIds ?? []).join(','),
];

/**
 * Cross-request cached filter dictionaries for SSR catalog / permalink pages.
 * Uses the session-less `api` client so responses are safe to share in the Data Cache.
 * React.cache dedupes within a single render (generateMetadata + page).
 */
export const fetchFanficsFilterOptionsCached = cache(
  async (params: FetchFanficsFilterOptionsCachedParams): Promise<FanficsFilterOptions> => {
    return unstable_cache(
      async () =>
        fetchFanficsFilterOptions({
          serverApi: api,
          type: params.type,
          pageKind: params.pageKind,
          fetchMode: params.fetchMode,
          lockedFacets: params.lockedFacets,
          selectedCharacterIds: params.selectedCharacterIds,
          selectedTagIds: params.selectedTagIds,
        }),
      buildFilterOptionsCacheKeyParts(params),
      { revalidate: FILTER_OPTIONS_REVALIDATE_SECONDS },
    )();
  },
);
